import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { TelegramLink } from "../../common/TelegramLink";
import { QueryService } from "../../../services/QueryService";
import {
  markAfishaBootSuccess,
  reportAfishaLoadError,
} from "../../../services/clientAlert";
import "./AfishaPage.scss";

type ChannelItem = {
  id: number;
  name: string;
  title: string;
  info: string;
  imgUrl: string | null;
  tgUrl: string | null;
};

type ChannelsResponse = {
  type: string;
  data: {
    channels: ChannelItem[];
  };
};

const TelegramIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.28l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" />
  </svg>
);

const ChannelAvatar = ({ channel }: { channel: ChannelItem }) => {
  const [imgError, setImgError] = useState(false);
  const imgSrc = channel.imgUrl
    ? `${QueryService.getServerUri()}${channel.imgUrl}`
    : null;

  if (!imgSrc || imgError) {
    return (
      <span className="afisha-channel__avatar afisha-channel__avatar--placeholder">
        {channel.title.charAt(0).toUpperCase()}
      </span>
    );
  }

  return (
    <img
      className="afisha-channel__avatar"
      src={imgSrc}
      alt=""
      loading="lazy"
      onError={() => setImgError(true)}
    />
  );
};

export const AfishaChannelsPage = () => {
  const [channels, setChannels] = useState<ChannelItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredChannels = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return channels;

    return channels.filter(
      (channel) =>
        channel.title.toLowerCase().includes(query) ||
        channel.name.toLowerCase().includes(query),
    );
  }, [channels, searchQuery]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError("");
      try {
        const resp = await QueryService.postAfishaQuery("/api/channels/list");
        const payload = resp?.data as ChannelsResponse | undefined;
        if (cancelled) return;
        setChannels(payload?.data?.channels ?? []);
      } catch {
        if (!cancelled) {
          const loadError = "Не удалось загрузить список каналов";
          setError(loadError);
          reportAfishaLoadError(loadError);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!loading && !error) {
      markAfishaBootSuccess();
    }
  }, [loading, error]);

  return (
    <div className="afisha__channels-page">
      <div className="afisha__channels-inner">
        {loading && <p className="afisha__status">Загрузка…</p>}
        {error && (
          <p className="afisha__status afisha__status--error">{error}</p>
        )}

        {!loading && !error && (
          <>
            <label className="afisha__channels-search">
              <span className="afisha__channels-search-label">Поиск канала</span>
              <input
                type="search"
                className="afisha__channels-search-input"
                placeholder="Название или @username"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                autoComplete="off"
                spellCheck={false}
              />
            </label>

            <div className="afisha__channels-list" aria-live="polite">
              {channels.length === 0 ? (
                <p className="afisha__empty">Каналы пока не добавлены</p>
              ) : filteredChannels.length > 0 ? (
                filteredChannels.map((channel) => (
                  <article key={channel.id} className="afisha-channel">
                    <div className="afisha-channel__row">
                      <ChannelAvatar channel={channel} />

                      <span
                        className="afisha-channel__title"
                        title={channel.title}
                      >
                        {channel.title}
                      </span>

                      <div className="afisha-channel__actions">
                        <Link
                          to={`/channels/${encodeURIComponent(channel.name)}`}
                          className="afisha-channel__btn afisha-channel__btn--afisha"
                          title="Открыть афишу"
                        >
                          Открыть афишу
                        </Link>

                        {channel.tgUrl ? (
                          <TelegramLink
                            className="afisha-channel__btn afisha-channel__btn--tg"
                            href={channel.tgUrl}
                            aria-label={`Открыть ${channel.title} в Telegram`}
                            title="Открыть в Telegram"
                          >
                            <TelegramIcon />
                          </TelegramLink>
                        ) : null}
                      </div>
                    </div>
                  </article>
                ))
              ) : (
                <p className="afisha__empty">Ничего не найдено</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
